// src/components/SiriusConnect365Licenses.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Shield, ArrowRight, CreditCard, Users, FileText, Calendar, BarChart, Clock, ChevronDown } from 'lucide-react';

const SiriusConnect365Licenses = () => {
  const [selectedTier, setSelectedTier] = useState('basic');
  const [showComparison, setShowComparison] = useState(false);
  
  const handleTierChange = (tier) => {
    setSelectedTier(tier);
  };
  
  const toggleComparison = () => {
    setShowComparison(!showComparison);
  };
  
  // Features by tier
  const tiers = {
    basic: {
      name: 'Basic',
      price: '£0.5',
      description: 'Essential employee self-service for payslip access',
      features: [
        { name: 'Profile page visibility', included: true },
        { name: 'Payslip list view', included: true },
        { name: 'Payslip preview', included: true },
        { name: 'Payslip download (PDF)', included: true },
        { name: 'Microsoft 365 authentication', included: true },
        { name: 'Business Central integration', included: true },
        { name: 'Dashboard', included: false },
        { name: 'Document management', included: false },
        { name: 'Leave management', included: false },
        { name: 'Time tracking', included: false },
        { name: 'Reporting & analytics', included: false },
        { name: 'Starter checklist', included: false }
      ]
    },
    essential: {
      name: 'Essential',
      price: '£5',
      description: 'Complete employee portal with expanded features',
      features: [
        { name: 'Profile page visibility', included: true },
        { name: 'Payslip list view', included: true },
        { name: 'Payslip preview', included: true },
        { name: 'Payslip download (PDF)', included: true },
        { name: 'Microsoft 365 authentication', included: true },
        { name: 'Business Central integration', included: true },
        { name: 'Dashboard', included: true },
        { name: 'Document management', included: true },
        { name: 'Leave management', included: true },
        { name: 'Time tracking', included: true },
        { name: 'Reporting & analytics', included: true },
        { name: 'Starter checklist', included: false }
      ]
    },
    premium: {
      name: 'Premium',
      price: '£7',
      description: 'Full-featured solution with advanced capabilities',
      features: [
        { name: 'Profile page visibility', included: true },
        { name: 'Payslip list view', included: true },
        { name: 'Payslip preview', included: true },
        { name: 'Payslip download (PDF)', included: true },
        { name: 'Microsoft 365 authentication', included: true },
        { name: 'Business Central integration', included: true },
        { name: 'Dashboard', included: true },
        { name: 'Document management', included: true },
        { name: 'Leave management', included: true },
        { name: 'Time tracking', included: true },
        { name: 'Reporting & analytics', included: true },
        { name: 'Starter checklist', included: true }
      ]
    }
  };
  
  return (
    <div>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <svg className="h-8 w-8 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="ml-2 text-xl font-semibold tracking-tight text-gray-900">Business Central</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/register" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100">
                Register
              </Link>
              <Link to="/login" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      {/* License Plans Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Choose the right plan for your organization
            </h2>
            <p className="mt-4 text-xl text-gray-500 max-w-3xl mx-auto">
              SiriusConnect365 offers flexible license options to meet the specific needs of your business
            </p>
          </div>
          
          {/* License Tier Selection Tabs - Desktop */}
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex justify-center space-x-8">
                <button
                  onClick={() => handleTierChange('basic')}
                  className={`${
                    selectedTier === 'basic'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Basic
                </button>
                <button
                  onClick={() => handleTierChange('essential')}
                  className={`${
                    selectedTier === 'essential'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Essential
                </button>
                <button
                  onClick={() => handleTierChange('premium')}
                  className={`${
                    selectedTier === 'premium'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Premium
                </button>
              </nav>
            </div>
          </div>
          
          {/* License Tier Dropdown - Mobile */}
          <div className="sm:hidden mb-8">
            <div className="relative">
              <select
                value={selectedTier}
                onChange={(e) => handleTierChange(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="basic">Basic</option>
                <option value="essential">Essential</option>
                <option value="premium">Premium</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <ChevronDown className="h-4 w-4" />
              </div>
            </div>
          </div>
          
          {/* Selected License Tier Details */}
          <div className="mt-8 lg:mt-12">
            {selectedTier === 'basic' && (
              <div className="bg-white rounded-lg shadow-xl overflow-hidden lg:grid lg:grid-cols-2 lg:gap-0">
                <div className="pt-12 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:pr-0 xl:py-20 xl:px-20">
                  <div className="lg:self-center">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                      <span className="block">Basic License</span>
                    </h2>
                    <p className="mt-4 text-lg leading-6 text-gray-500">
                      Essential employee self-service for payslip access, ideal for small businesses looking for core functionality.
                    </p>
                    <div className="mt-8">
                      <div className="flex items-center">
                        <h3 className="flex-shrink-0 pr-4 text-4xl font-extrabold text-indigo-600 tracking-tight">
                          £0.5
                        </h3>
                        <div className="text-sm text-gray-500">
                          <p>per employee/month</p>
                          <p>billed monthly</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-8">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Employee profile visibility
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Payslip list and detailed preview
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Secure PDF payslip download
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Microsoft 365 authentication
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-10">
                      <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                        Get started with Basic
                        <ArrowRight className="ml-3 h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="relative pt-6 pb-12 px-6 sm:pt-8 sm:px-12 lg:py-16 lg:pl-0 xl:py-20 xl:pl-8">
                  <div className="relative lg:ml-0 h-full flex flex-col justify-center">
                    <div className="bg-gray-50 rounded-lg p-8 h-full">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Designed for employees who need:</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Secure access</strong> to personal payroll information
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Self-service access</strong> to current and historical payslips
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Easy download</strong> of payslips for tax filing and record-keeping
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Seamless login</strong> with existing Microsoft 365 credentials
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {selectedTier === 'essential' && (
              <div className="bg-white rounded-lg shadow-xl overflow-hidden lg:grid lg:grid-cols-2 lg:gap-0">
                <div className="pt-12 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:pr-0 xl:py-20 xl:px-20">
                  <div className="lg:self-center">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                      <span className="block">Essential License</span>
                      <span className="block text-sm font-semibold text-indigo-500 mt-1">MOST POPULAR</span>
                    </h2>
                    <p className="mt-4 text-lg leading-6 text-gray-500">
                      Complete employee portal with expanded features for comprehensive HR self-service capabilities.
                    </p>
                    <div className="mt-8">
                      <div className="flex items-center">
                        <h3 className="flex-shrink-0 pr-4 text-4xl font-extrabold text-indigo-600 tracking-tight">
                          £5
                        </h3>
                        <div className="text-sm text-gray-500">
                          <p>per employee/month</p>
                          <p>billed monthly</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-8">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>All Basic features</strong> included
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Personalized employee dashboard
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Document management system
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Leave management and time tracking
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Reporting and analytics tools
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-10">
                      <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                        Get started with Essential
                        <ArrowRight className="ml-3 h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="relative pt-6 pb-12 px-6 sm:pt-8 sm:px-12 lg:py-16 lg:pl-0 xl:py-20 xl:pl-8">
                  <div className="relative lg:ml-0 h-full flex flex-col justify-center">
                    <div className="bg-indigo-50 rounded-lg p-8 h-full">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Perfect for organizations that need:</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Comprehensive dashboard</strong> for employee self-service
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Centralized document repository</strong> for important employee documents
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Leave and absence management</strong> to streamline time-off requests
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Advanced reporting</strong> for better workforce insights
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Time tracking capabilities</strong> for accurate attendance records
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {selectedTier === 'premium' && (
              <div className="bg-white rounded-lg shadow-xl overflow-hidden lg:grid lg:grid-cols-2 lg:gap-0">
                <div className="pt-12 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:pr-0 xl:py-20 xl:px-20">
                  <div className="lg:self-center">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                      <span className="block">Premium License</span>
                    </h2>
                    <p className="mt-4 text-lg leading-6 text-gray-500">
                      Our most comprehensive solution with advanced features including the employee starter checklist.
                    </p>
                    <div className="mt-8">
                      <div className="flex items-center">
                        <h3 className="flex-shrink-0 pr-4 text-4xl font-extrabold text-indigo-600 tracking-tight">
                          £7
                        </h3>
                        <div className="text-sm text-gray-500">
                          <p>per employee/month</p>
                          <p>billed monthly</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-8">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>All Basic & Essential features</strong> included
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Comprehensive starter checklist
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Advanced onboarding tools
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Enhanced reporting capabilities
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            Priority support
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-10">
                      <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                        Get started with Premium
                        <ArrowRight className="ml-3 h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="relative pt-6 pb-12 px-6 sm:pt-8 sm:px-12 lg:py-16 lg:pl-0 xl:py-20 xl:pl-8">
                  <div className="relative lg:ml-0 h-full flex flex-col justify-center">
                    <div className="bg-purple-50 rounded-lg p-8 h-full">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Ideal for organizations that want:</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Complete employee lifecycle management</strong> from onboarding to offboarding
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Comprehensive starter checklist</strong> to streamline new employee setup
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Advanced document workflows</strong> for approvals and digital signatures
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Enhanced analytics and reporting</strong> with customizable dashboards
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">
                            <strong>Priority support</strong> with dedicated account management
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Feature Comparison */}
          <div className="mt-12">
            <button 
              onClick={toggleComparison} 
              className="flex items-center justify-center mx-auto px-4 py-2 text-base font-medium text-indigo-600 hover:text-indigo-500"
            >
              {showComparison ? 'Hide detailed comparison' : 'View detailed comparison'}
              <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${showComparison ? 'transform rotate-180' : ''}`} />
            </button>
            
            {showComparison && (
              <div className="mt-6 overflow-hidden">
                <div className="max-w-4xl mx-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          Feature
                        </th>
                        <th scope="col" className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Basic
                        </th>
                        <th scope="col" className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Essential
                        </th>
                        <th scope="col" className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Premium
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(tiers.basic.features).map(([index, feature]) => (
                        <tr key={index} className={parseInt(index) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="py-4 text-sm font-medium text-gray-900">
                            {feature.name}
                          </td>
                          <td className="py-4 text-center">
                            {tiers.basic.features[index].included ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                          <td className="py-4 text-center">
                            {tiers.essential.features[index].included ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                          <td className="py-4 text-center">
                            {tiers.premium.features[index].included ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Mock License Access UI Section - How it works */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">How license-based access works</h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              SiriusConnect365 intelligently adapts to your organization's license tier, showing only the features your employees have access to.
            </p>
          </div>
          
          <div className="mt-16 lg:mt-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {/* Step 1 */}
              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                  <span className="text-xl font-bold">1</span>
                </div>
                <div className="ml-16">
                  <h3 className="text-lg font-medium text-gray-900">User Authentication</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Employees sign in using their Microsoft 365 credentials, providing a seamless and secure authentication experience.
                  </p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                  <span className="text-xl font-bold">2</span>
                </div>
                <div className="ml-16">
                  <h3 className="text-lg font-medium text-gray-900">License Detection</h3>
                  <p className="mt-2 text-base text-gray-500">
                    The system automatically detects the organization's license tier (Basic, Essential, or Premium) from Business Central.
                  </p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                  <span className="text-xl font-bold">3</span>
                </div>
                <div className="ml-16">
                  <h3 className="text-lg font-medium text-gray-900">Personalized Experience</h3>
                  <p className="mt-2 text-base text-gray-500">
                    The portal dynamically adapts to show only the features and functions available in your license tier.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-16 flex justify-center">
              <div className="relative max-w-5xl">
                <div className="rounded-lg overflow-hidden shadow-xl border border-gray-200">
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded-full bg-red-400"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-400"></div>
                      <div className="h-3 w-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="text-xs text-gray-500">SiriusConnect365 Employee Portal</div>
                    <div></div>
                  </div>
                  <div className="bg-white">
                    <img 
                      src="/api/placeholder/800/450" 
                      alt="SiriusConnect365 License Visualization" 
                      className="w-full object-cover"
                    />
                  </div>
                </div>
                
                <div className="absolute -top-4 -right-4 bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full">
                  Visibility adapts to license tier
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="grid grid-cols-2 gap-8 xl:col-span-2">
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Solutions</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Payroll Management
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Employee Self-Service
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Document Management
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        HR Analytics
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Support</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Documentation
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        API Reference
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Guides
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Help Center
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Company</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        About
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Blog
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Careers
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Contact
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Legal</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Privacy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Terms
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Cookie Policy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        GDPR
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8 xl:mt-0">
              <div className="flex items-center">
                <svg className="h-10 w-10 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <h3 className="ml-2 text-xl font-semibold text-gray-900">Business Central</h3>
              </div>
              <p className="mt-4 text-base text-gray-500">
                The premier employee portal solution for Business Central users. Simplify HR operations and enhance employee experience.
              </p>
              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Subscribe to our newsletter</p>
                <div className="mt-2 flex">
                  <input
                    aria-label="Email address"
                    type="email"
                    required
                    className="appearance-none min-w-0 w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-4 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:placeholder-gray-400"
                    placeholder="Enter your email"
                  />
                  <div className="ml-3 rounded-md shadow">
                    <button className="w-full flex items-center justify-center py-2 px-4 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-200 pt-8">
            <p className="text-base text-gray-400 xl:text-center">
              &copy; 2025 SiriusConnect365. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiriusConnect365Licenses;